import React, { FC, useCallback, ChangeEvent, FocusEvent, ForwardedRef } from 'react';

import { FixedKeyboardEvent } from 'ia-react-core/~/interfaces/FixedKeyboardEvent';
import { ElementWithValue } from 'ia-react-core/~/interfaces/ElementWithValue';
import excludePropsByComponent from '../../../utils/components/_excludePropsByComponent';

import Control, { ControlProps, ControlRenderProps } from '../Control';
import TextInput, { TextInputProps } from '../TextInput';

export type PostalCodeControlValidationProps = {
  /** Adds the `required` validator to the control. The control will be invalid if the value is empty. */
  required?: boolean;
  /** Adds the `recommended` validator to the control. The control will display a warning if the value is empty. */
  recommended?: boolean;
};

export interface PostalCodeControlProps extends
    Omit<TextInputProps, 'defaultValue' | 'name' | 'value' | 'id' | 'isValid' | 'isTouched'>,
    Omit<ControlProps<string, string, HTMLInputElement, PostalCodeControlValidationProps>, 'children'>,
    PostalCodeControlValidationProps {
  ref?: ForwardedRef<HTMLInputElement>;
}

interface PostalCodeControlInternalProps extends Omit<ControlRenderProps<string, HTMLInputElement>, 'onChange' | 'onBlur' | 'onFocus' | 'onKeyDown' | 'id'> {
  onChangeCallback: ControlRenderProps<string, HTMLInputElement>['onChange'];
  onBlurCallback: ControlRenderProps<string, HTMLInputElement>['onBlur'];
  onKeyDownCallback: ControlRenderProps<string, HTMLInputElement>['onKeyDown'];
}

export interface PostalCodeControlComposedProps extends PostalCodeControlProps, PostalCodeControlInternalProps {}

export interface PostalCodeControlStatics {
  labeledControl: boolean;
}

export const PostalCodeControlDefaultProps: Partial<PostalCodeControlProps> = {
  valueToStateFormatters: [],
  stateToValueFormatters: [],
};

const PostalCodeControl: FC<PostalCodeControlComposedProps> & PostalCodeControlStatics = ({
  id,
  name,
  required,
  readOnly,
  textOnly,
  validators,
  valueToStateFormatters,
  stateToValueFormatters,
  isValid,
  isTouched,
  value,
  errorMessagesId,
  inputRef,
  onChange,
  onBlur,
  onKeyDown,
  onChangeCallback,
  onBlurCallback,
  onKeyDownCallback,
  ...props
}) => {
  const handleChange = useCallback((event: ChangeEvent<ElementWithValue<string>>) => {
    onChangeCallback(event);
    if (onChange) onChange(event as ChangeEvent<HTMLInputElement>);
  }, [onChange, onChangeCallback]);

  const handleBlur = useCallback((event: FocusEvent<ElementWithValue<string>>) => {
    onBlurCallback(event);
    if (onBlur) onBlur(event as FocusEvent<HTMLInputElement>);
  }, [onBlur, onBlurCallback]);

  const handleKeydown = useCallback((event: FixedKeyboardEvent) => {
    onKeyDownCallback(event);
    if (onKeyDown) onKeyDown(event);
  }, [onKeyDownCallback, onKeyDown]);

  return (
    <TextInput
      id={id}
      data-control
      data-testid="PostalCodeControl"
      aria-invalid={Boolean(!isValid && isTouched)}
      aria-errormessage={errorMessagesId}
      data-control-nesting="1"
      readOnly={readOnly}
      textOnly={textOnly}
      {...excludePropsByComponent(props, Control)}
      name={name}
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={handleKeydown}
      isValid={isValid}
      isTouched={isTouched}
      inputRef={inputRef}
    />
  );
};

PostalCodeControl.labeledControl = true;
PostalCodeControl.defaultProps = PostalCodeControlDefaultProps;

export default PostalCodeControl;
